"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ServiceQuoteSection from "@/components/service/ServiceQuoteSection";
import ServiceFormSuccess from "@/components/service/ServiceFormSuccess";
import { inputClass, textareaClass, selectClass, labelClass, submitClass, formCardClass } from "@/components/service/formStyles";
function WallCleaningQuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <ServiceFormSuccess title="Quote Request Sent!" message={successMessage || "Thank you for your inquiry! We have received your details and will be in touch shortly with your free quote."} onReset={resetForm} resetLabel="Request Another Quote" />;
  }
  return <Form onSubmit={handleSubmit} className={formCardClass}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="07700 900000" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="postcode" rules={{
        required: "Postcode is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Postcode</FormLabel>
              <FormControl>
                <Input placeholder="e.g. SO15 1AA" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="propertyType" rules={{
        required: "Please select a property type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Property Type</FormLabel>
              <FormControl>
                <select {...field} className={selectClass}>
                  <option value="">Select property type...</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="serviceType" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Service Required</FormLabel>
              <FormControl>
                <select {...field} className={selectClass}>
                  <option value="">Select a service...</option>
                  <option value="high-pressure-washing">High-Pressure Washing</option>
                  <option value="algae-removal">Algae Removal</option>
                  <option value="soft-wash-systems">Soft Wash Systems</option>
                  <option value="render-cleaning">Render Cleaning</option>
                  <option value="brick-stone-restoration">Brick & Stone Restoration</option>
                  <option value="driveway-patio-walls">Driveway & Patio Walls</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="description" rules={{
      required: "Please provide some details about your project"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Project Description</FormLabel>
            <FormControl>
              <textarea placeholder="Please describe what needs cleaning, approximate size, and any specific issues..." className={textareaClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={submitClass}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : "Get Your Free Quote"}
      </Button>
    </Form>;
}
export default function WallCleaningQuote() {
  return <ServiceQuoteSection id="get-a-free-quote" eyebrow="GET A FREE QUOTE" heading="Get a Free Wall Cleaning Quote" intro="Fill out the form below with your project details, and our team will get back to you promptly with a comprehensive, no-obligation quote across Southampton, Hampshire & surrounding areas." serviceName="Wall Cleaning">
      <FormProvider formId="get-a-free-quote-form" sectionName="Get a Free Quote" defaultValues={{
      name: "",
      email: "",
      phone: "",
      postcode: "",
      propertyType: "",
      serviceType: "",
      description: ""
    }} submitText="Get Your Free Quote" successMessage="Thank you for your inquiry! We have received your details and will be in touch shortly with your free quote.">
        <WallCleaningQuoteForm />
      </FormProvider>
    </ServiceQuoteSection>;
}